import { cn } from '@/utils'
import { forwardRef, type MouseEvent, type ReactNode, useCallback, useImperativeHandle, useLayoutEffect, useRef } from 'react'
import { createPortal } from 'react-dom'

export interface FloatingPanelRef {
  setHeight: (height: number, options?: { immediate?: boolean }) => void
}

interface FloatingPanelProps {
  anchors: number[]
  children: ReactNode
  className?: string
  header?: ReactNode
  onHeightChange?: (height: number, animating: boolean) => void
}

const nearest = (arr: readonly number[], target: number) =>
  arr.reduce((pre, cur) => (Math.abs(pre - target) < Math.abs(cur - target) ? pre : cur))

const scrollBehavior = (immediate?: boolean) => (immediate ? 'instant' : 'smooth') as ScrollBehavior

export const FloatingPanel = forwardRef<FloatingPanelRef, FloatingPanelProps>(
  ({ anchors, children, onHeightChange, header, className }, ref) => {
    const maxHeight = anchors[anchors.length - 1] ?? (typeof window === 'undefined' ? 0 : window.innerHeight)
    const minHeight = anchors[0] ?? 0
    const defaultAnchor = anchors[Math.min(1, anchors.length - 1)] ?? minHeight
    const anchorKey = anchors.join(',')

    const hostRef = useRef<HTMLDivElement>(null)
    const sheetRef = useRef<HTMLDivElement>(null)
    const onHeightChangeRef = useRef(onHeightChange)
    onHeightChangeRef.current = onHeightChange

    const scrollToHeight = useCallback((height: number, immediate?: boolean) => {
      hostRef.current?.scrollTo({
        top: Math.max(0, height - minHeight),
        behavior: scrollBehavior(immediate),
      })
    }, [minHeight])

    useImperativeHandle(
      ref,
      () => ({
        setHeight: (height, options) => scrollToHeight(height, options?.immediate),
      }),
      [scrollToHeight],
    )

    useLayoutEffect(() => {
      const host = hostRef.current
      const sheet = sheetRef.current
      if (!host || !sheet) return

      const currentAnchors = anchorKey.split(',').map(Number)
      let frame = 0
      let scrollEndTimer = 0

      const snapHeight = () => nearest(currentAnchors, minHeight + host.scrollTop)
      const setExpanded = (height: number) => {
        sheet.dataset.expanded = String(height >= maxHeight - 1)
      }
      const reportFinal = () => {
        const height = snapHeight()
        setExpanded(height)
        onHeightChangeRef.current?.(height, false)
      }
      const scheduleFinal = () => {
        window.clearTimeout(scrollEndTimer)
        scrollEndTimer = window.setTimeout(reportFinal, 120)
      }
      const handleScroll = () => {
        scheduleFinal()
        if (frame) return

        frame = window.requestAnimationFrame(() => {
          frame = 0
          const height = minHeight + host.scrollTop
          setExpanded(height)
          onHeightChangeRef.current?.(height, true)
        })
      }

      host.addEventListener('scroll', handleScroll, { passive: true })
      host.addEventListener('scrollend', reportFinal)
      scrollToHeight(defaultAnchor, true)
      reportFinal()

      return () => {
        host.removeEventListener('scroll', handleScroll)
        host.removeEventListener('scrollend', reportFinal)
        window.clearTimeout(scrollEndTimer)
        if (frame) window.cancelAnimationFrame(frame)
      }
    }, [anchorKey, defaultAnchor, maxHeight, minHeight, scrollToHeight])

    const handleHeaderClick = (event: MouseEvent<HTMLDivElement>) => {
      if (event.target instanceof Element && event.target.closest('button, a, [role="button"]')) return

      const host = hostRef.current
      if (!host) return

      const currentIndex = anchors.indexOf(nearest(anchors, minHeight + host.scrollTop))
      scrollToHeight(anchors[(currentIndex + 1) % anchors.length])
    }

    return createPortal(
      <div
        ref={hostRef}
        className="floating-panel-host fixed inset-0 z-[999] overflow-y-scroll overscroll-y-none pointer-events-none"
        style={{ scrollSnapType: 'y mandatory' }}
      >
        <div
          className="relative"
          style={{ height: `calc(100dvh - ${minHeight}px + ${maxHeight}px)` }}
        >
          {anchors.map((anchor) => (
            <div
              key={anchor}
              className="absolute h-px w-px"
              style={{ top: anchor - minHeight, scrollSnapAlign: 'start' }}
            />
          ))}
          <div
            ref={sheetRef}
            className={cn(
              'floating-panel-sheet pointer-events-auto absolute left-0 flex w-full flex-col rounded-t-xl bg-white',
              className,
            )}
            style={{
              top: `calc(100dvh - ${minHeight}px)`,
              height: maxHeight,
            }}
            data-expanded="false"
          >
            <div className="floating-panel-header sticky top-0 z-10 shrink-0 select-none rounded-t-xl bg-white">
              <div className="flex h-5 items-center justify-center rounded-t-xl bg-white" onClick={handleHeaderClick}>
                <div className="h-[3px] w-5 rounded-full bg-gray-300" />
              </div>
              {header}
            </div>
            <div className="floating-panel-content min-h-0 flex-1 overflow-y-hidden touch-pan-y bg-white">
              {children}
            </div>
          </div>
        </div>
      </div>,
      document.body,
    )
  },
)

FloatingPanel.displayName = 'FloatingPanel'
